import React from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Card,
  CardContent,
  RadioGroup,
  Radio,
  FormControlLabel,
  Chip,
  Divider,
} from "@mui/material";
import {
  convertHeightToCm,
  convertWeightToKg,
  getAgeFromBirthday,
} from "../../../../data/GlobalData";

import StandardButton from "../../../../components/StandardButton";
import FormField from "../../../../components/atoms/FormField";
import TabPanel from "../../../../components/molecules/TabPanel";
import {
  ActivityLevel,
  ActivityLevelValues,
  ActivityLevelLabel,
  WeightGoal,
  WeightGoalLabel,
  WeightGoalFactor,
} from "../../../../data/Types";
import useHealthReport from "../../../../hooks/useHealthReport";
import useOrganizationData from "../../../../hooks/useOrganizationData";
import useHistory from "../../../../hooks/useHistory";
import moment from "moment";
import NewColors from "../../../../themes/NewColors";
const MacroSelectSimpleDialog = ({
  client,
  nutritionGoal,
  open,
  isEdit,
  onDialogClosed,
}) => {
  const { report } = useHealthReport({
    uid: client.uid,
    filter: "fatPercentage",
    operation: "!=",
    value: 0,
  });

  const { organizationData: organizationSettings } = useOrganizationData({
    uid: client.uid,
  });
  const isImperial = organizationSettings?.unitSystem === "imperial";

  const [openDialog, setOpenDialog] = React.useState(false);

  const [macros, setMacros] = React.useState(nutritionGoal);

  const storedMethod = localStorage.getItem("storedMethod");
  const storedFatPercentage = localStorage.getItem("fatPercentage");
  const storedCarbSplit = localStorage.getItem("carbSplit");
  const storedFatSplit = localStorage.getItem("fatSplit");
  const storedProteinSplit = localStorage.getItem("proteinSplit");
  const storedWeightGoal = localStorage.getItem("weightGoal");

  const [method, setMethod] = React.useState(storedMethod || "body-fat");

  const [fatPercentage, setFatPercentage] = React.useState(
    storedFatPercentage || 25,
  );
  const [carbSplit, setCarbSplit] = React.useState(
    parseInt(storedCarbSplit) || 35,
  );
  const [fatSplit, setFatSplit] = React.useState(
    parseInt(storedFatSplit) || 30,
  );
  const [proteinSplit, setProteinSplit] = React.useState(
    parseInt(storedProteinSplit) || 35,
  );
  const [weightGoal, setWeightGoal] = React.useState(
    storedWeightGoal || WeightGoal.maintain,
  );
  const [selectedPreset, setSelectedPreset] = React.useState(null);
  const [activeTab, setActiveTab] = React.useState(0);
  const [validationErrors, setValidationErrors] = React.useState({});

  // Preset macro ratios
  const MACRO_PRESETS = [
    {
      name: "Balanced",
      description: "General fitness",
      carbs: 40,
      protein: 30,
      fat: 30,
      color: NewColors.charts.blue,
    },
    {
      name: "Low Carb",
      description: "Fat loss focus",
      carbs: 20,
      protein: 40,
      fat: 40,
      color: NewColors.brand.primary,
    },
    {
      name: "High Protein",
      description: "Muscle building",
      carbs: 30,
      protein: 45,
      fat: 25,
      color: NewColors.nutrition.fats,
    },
    {
      name: "Endurance",
      description: "Cardio athletes",
      carbs: 55,
      protein: 20,
      fat: 25,
      color: NewColors.nutrition.carbs,
    },
  ];

  const totalkCal = React.useMemo(() => {
    if (!macros) {
      return 0;
    }
    const proteinskCal = macros.proteins * 4;
    const carbskCal = macros.carbs * 4;
    const fatskCal = macros.fats * 9;

    return proteinskCal + carbskCal + fatskCal;
  }, [macros]);

  const validateForm = React.useCallback(() => {
    const errors = {};

    // Validate name
    if (!macros.name || macros.name.trim().length === 0) {
      errors.name = "Goal name is required";
    } else if (macros.name.trim().length < 2) {
      errors.name = "Goal name must be at least 2 characters";
    }

    // Validate that macros are calculated (have actual gram values)
    if (!macros.carbs || macros.carbs === 0) {
      errors.carbs = "Carbs must be calculated and greater than 0";
    }

    if (!macros.proteins || macros.proteins === 0) {
      errors.proteins = "Proteins must be calculated and greater than 0";
    }

    if (!macros.fats || macros.fats === 0) {
      errors.fats = "Fats must be calculated and greater than 0";
    }

    // If no macro values, suggest using calculator
    if (
      (!macros.carbs || macros.carbs === 0) &&
      (!macros.proteins || macros.proteins === 0) &&
      (!macros.fats || macros.fats === 0)
    ) {
      errors.macros =
        "Please calculate macro values using the Calculator tab or enter values manually in the Manual tab";
    }

    // Validate water intake (optional but should be reasonable if provided)
    if (isImperial) {
      if (macros.water && (macros.water < 17 || macros.water > 338)) {
        errors.water = "Water intake should be between 17–338 oz";
      }
    } else {
      if (macros.water && (macros.water < 500 || macros.water > 10000)) {
        errors.water = "Water intake should be between 500–10000 mL";
      }
    }

    if (macros.fiber && (macros.fiber < 0 || macros.fiber > 200)) {
      errors.fiber = "Fiber goal should be between 0-200 g";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [macros]);

  React.useEffect(() => {
    setOpenDialog(open);

    if (nutritionGoal) {
      const storedWaterMl = nutritionGoal.water ?? 0;
      setMacros({
        ...nutritionGoal,
        fiber: nutritionGoal.fiber ?? 0,
        water: isImperial
          ? Math.round((storedWaterMl / 29.5735) * 10) / 10
          : storedWaterMl,
      });
    }

    // Clear validation errors when dialog opens
    if (open) {
      setValidationErrors({});
    }
  }, [open, nutritionGoal, isImperial]); // eslint-disable-line

  React.useEffect(() => {
    localStorage.setItem("fatPercentage", fatPercentage);
    localStorage.setItem("carbSplit", carbSplit);
    localStorage.setItem("fatSplit", fatSplit);
    localStorage.setItem("proteinSplit", proteinSplit);
    localStorage.setItem("weightGoal", weightGoal);
    localStorage.setItem("storedMethod", method);
  }, [fatPercentage, carbSplit, fatSplit, proteinSplit, weightGoal, method]);

  React.useEffect(() => {
    if (report) {
      const { reportData } = report;
      if (reportData.fatPercentage) {
        setFatPercentage(reportData.fatPercentage);
      }
    }
  }, [report]);

  const foundWeight = React.useMemo(() => {
    if (client.statistics && client.statistics.currentWeight) {
      if (client.statistics.currentWeight > 0) {
        return client.statistics.currentWeight;
      }
    }

    if (client.statistics && client.statistics.startingWeight) {
      if (client.statistics.startingWeight > 0) {
        return client.statistics.startingWeight;
      }
    }

    if (client.statistics && client.statistics.weight) {
      if (client.statistics.weight > 0) {
        return client.statistics.weight;
      }
    }
  }, [client.statistics]);

  const calculatedValues = React.useMemo(() => {
    const sex = client.statistics.sex;
    const height = convertHeightToCm(client.statistics.height) || 0;
    let system = "imperial";
    if (organizationSettings && organizationSettings.unitSystem) {
      system = organizationSettings.unitSystem;
    }
    let weightKg = 0;
    if (system === "imperial") {
      weightKg = convertWeightToKg(foundWeight || 0);
    } else {
      weightKg = foundWeight || 0;
    }

    let age = 0;
    if (client.statistics.birthDate) {
      age = getAgeFromBirthday(client.statistics.birthDate.toDate());
    }

    let bmr1 = 0;
    if (sex === "male" && weightKg > 0 && height > 0 && age > 0) {
      bmr1 = 66.5 + 13.75 * weightKg + 5.003 * height - 6.775 * age;
    }
    if (sex === "female" && weightKg > 0 && height > 0 && age > 0) {
      bmr1 = 655.1 + 9.563 * weightKg + 1.85 * height - 4.676 * age;
    }

    const validFatPercentage = parseFloat(fatPercentage) || 25;
    const lbm =
      weightKg > 0 ? (weightKg * (100 - validFatPercentage)) / 100 : 0;

    const bmr2 = lbm > 0 ? 370 + 21.6 * lbm : 0;

    const activityMultiplier =
      client.statistics.activityLevel &&
      ActivityLevelValues[client.statistics.activityLevel]
        ? ActivityLevelValues[client.statistics.activityLevel]
        : 1.2; // Default to sedentary

    const adjustedbmr1 = bmr1 > 0 ? activityMultiplier * bmr1 : 0;
    const adjustedbmr2 = bmr2 > 0 ? activityMultiplier * bmr2 : 0;

    const weightGoalMultiplier =
      WeightGoalFactor[weightGoal] !== undefined
        ? WeightGoalFactor[weightGoal]
        : 0; // Default to maintain

    return {
      sex,
      height,
      weightKg,
      age,
      bmr1,
      bmr2,
      lbm,
      adjustedbmr1: adjustedbmr1,
      adjustedbmr2: adjustedbmr2,
      bmr1WeightFactor:
        adjustedbmr1 > 0
          ? adjustedbmr1 + adjustedbmr1 * weightGoalMultiplier
          : 0,
      bmr2WeightFactor:
        adjustedbmr2 > 0
          ? adjustedbmr2 + adjustedbmr2 * weightGoalMultiplier
          : 0,
    };
  }, [
    client.statistics,
    fatPercentage,
    weightGoal,
    foundWeight,
    organizationSettings,
  ]);

  const macroSplitTotal = React.useMemo(() => {
    return carbSplit + fatSplit + proteinSplit;
  }, [carbSplit, fatSplit, proteinSplit]);

  /**
   * Calculate macros
   */
  const handleCalculateMacros = React.useCallback(() => {
    const { bmr2WeightFactor, bmr1WeightFactor } = calculatedValues;

    const factor = method === "body-fat" ? bmr2WeightFactor : bmr1WeightFactor;

    const proteins = Math.round((factor * (proteinSplit / 100)) / 4);
    const fats = Math.round((factor * (fatSplit / 100)) / 9);
    const carbs = Math.round((factor * (carbSplit / 100)) / 4);

    setMacros({
      ...macros,
      proteins: proteins,
      fats: fats,
      carbs: carbs,
    });

    // Clear macro validation errors since we just calculated them
    setValidationErrors((prev) => ({
      ...prev,
      carbs: undefined,
      proteins: undefined,
      fats: undefined,
      macros: undefined,
    }));
  }, [calculatedValues, fatSplit, proteinSplit, carbSplit, macros, method]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Intelligent macro adjustment based on method and goals
  const getIntelligentMacroSplit = React.useCallback(() => {
    const bodyFatPercentage = parseFloat(fatPercentage) || 25;
    let recommendedSplit = { carbs: 40, protein: 30, fat: 30 }; // Default balanced

    // Combine body fat percentage and weight goal for intelligent recommendations
    if (method === "body-fat" && bodyFatPercentage) {
      // Start with body fat-based recommendations
      if (bodyFatPercentage < 15) {
        // Very lean individuals - adjust based on goal
        switch (weightGoal) {
          case WeightGoal.lose:
            recommendedSplit = { carbs: 35, protein: 40, fat: 25 }; // Lean + fat loss
            break;
          case WeightGoal.lose_light:
            recommendedSplit = { carbs: 40, protein: 35, fat: 25 }; // Lean + moderate loss
            break;
          case WeightGoal.gain:
            recommendedSplit = { carbs: 55, protein: 25, fat: 20 }; // Lean + muscle gain
            break;
          case WeightGoal.gain_light:
            recommendedSplit = { carbs: 50, protein: 30, fat: 20 }; // Lean + lean gain
            break;
          case WeightGoal.maintain:
          default:
            recommendedSplit = { carbs: 45, protein: 30, fat: 25 }; // Lean maintenance
            break;
        }
      } else if (bodyFatPercentage > 25) {
        // Higher body fat - prioritize fat loss regardless of goal
        switch (weightGoal) {
          case WeightGoal.lose:
            recommendedSplit = { carbs: 15, protein: 50, fat: 35 }; // High BF + aggressive loss
            break;
          case WeightGoal.lose_light:
            recommendedSplit = { carbs: 25, protein: 45, fat: 30 }; // High BF + moderate loss
            break;
          case WeightGoal.gain:
            recommendedSplit = { carbs: 35, protein: 35, fat: 30 }; // High BF + muscle gain (moderate carbs)
            break;
          case WeightGoal.gain_light:
            recommendedSplit = { carbs: 30, protein: 40, fat: 30 }; // High BF + lean gain
            break;
          case WeightGoal.maintain:
          default:
            recommendedSplit = { carbs: 25, protein: 40, fat: 35 }; // High BF maintenance
            break;
        }
      } else {
        // Normal body fat (15-25%) - standard goal-based recommendations
        switch (weightGoal) {
          case WeightGoal.lose:
            recommendedSplit = { carbs: 25, protein: 45, fat: 30 };
            break;
          case WeightGoal.lose_light:
            recommendedSplit = { carbs: 35, protein: 40, fat: 25 };
            break;
          case WeightGoal.gain:
            recommendedSplit = { carbs: 50, protein: 25, fat: 25 };
            break;
          case WeightGoal.gain_light:
            recommendedSplit = { carbs: 45, protein: 30, fat: 25 };
            break;
          case WeightGoal.maintain:
          default:
            recommendedSplit = { carbs: 40, protein: 30, fat: 30 };
            break;
        }
      }
    } else {
      // Age/weight method - use standard goal-based recommendations
      switch (weightGoal) {
        case WeightGoal.lose:
          recommendedSplit = { carbs: 25, protein: 45, fat: 30 };
          break;
        case WeightGoal.lose_light:
          recommendedSplit = { carbs: 35, protein: 40, fat: 25 };
          break;
        case WeightGoal.gain:
          recommendedSplit = { carbs: 50, protein: 25, fat: 25 };
          break;
        case WeightGoal.gain_light:
          recommendedSplit = { carbs: 45, protein: 30, fat: 25 };
          break;
        case WeightGoal.maintain:
        default:
          recommendedSplit = { carbs: 40, protein: 30, fat: 30 };
          break;
      }
    }

    return recommendedSplit;
  }, [method, fatPercentage, weightGoal]);

  // Update macro splits when method or weight goal changes
  React.useEffect(() => {
    if (!selectedPreset) {
      // Only auto-adjust if no preset is selected
      const intelligentSplit = getIntelligentMacroSplit();
      setCarbSplit(intelligentSplit.carbs);
      setProteinSplit(intelligentSplit.protein);
      setFatSplit(intelligentSplit.fat);
    }
  }, [
    method,
    weightGoal,
    fatPercentage,
    selectedPreset,
    getIntelligentMacroSplit,
  ]);

  const handlePresetSelect = (preset) => {
    setSelectedPreset(preset.name);
    setCarbSplit(preset.carbs);
    setProteinSplit(preset.protein);
    setFatSplit(preset.fat);
    // Auto-calculate macros when preset is selected
    setTimeout(() => {
      if (preset.carbs + preset.protein + preset.fat === 100) {
        handleCalculateMacros();
      }
    }, 100);
  };

  const handleMethodChange = (event) => {
    setMethod(event.target.value);
    setSelectedPreset(null); // Clear preset when method changes
  };

  const handleWeightGoalChange = (event) => {
    setWeightGoal(event.target.value);
    setSelectedPreset(null); // Clear preset when weight goal changes
  };

  const handleFatPercentageChange = (event) => {
    setFatPercentage(event.target.value);
    setSelectedPreset(null); // Clear preset when body fat changes
  };

  if (!macros) {
    return null;
  }

  const PresetCard = ({ preset, isSelected, onClick }) => (
    <Card
      sx={{
        cursor: "pointer",
        border: isSelected ? `2px solid ${preset.color}` : "1px solid #e0e0e0",
        transition: "all 0.2s ease",
        "&:hover": {
          boxShadow: 3,
          borderColor: preset.color,
        },
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              backgroundColor: preset.color,
              mr: 1,
            }}
          />
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            {preset.name}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {preset.description}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Chip size="small" label={`${preset.carbs}% Carbs`} />
          <Chip size="small" label={`${preset.protein}% Protein`} />
          <Chip size="small" label={`${preset.fat}% Fat`} />
        </Box>
      </CardContent>
    </Card>
  );
  function HighlightText(e) {
    e.target.style.fontWeight = "bold";
  }
  function RemoveHighlight(e) {
    e.target.style.fontWeight = "normal";
  }

  const MacroDistributionPreview = () => {
    const total = carbSplit + proteinSplit + fatSplit;
    if (total === 0) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
          Macro Distribution
        </Typography>
        <Box
          sx={{
            display: "flex",
            height: 8,
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              backgroundColor: NewColors.nutrition.carbs,
              width: `${(carbSplit / total) * 100}%`,
            }}
          />
          <Box
            sx={{
              backgroundColor: NewColors.charts.blue,
              width: `${(proteinSplit / total) * 100}%`,
            }}
          />
          <Box
            sx={{
              backgroundColor: NewColors.nutrition.fats,
              width: `${(fatSplit / total) * 100}%`,
            }}
          />
        </Box>
        <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                backgroundColor: NewColors.nutrition.carbs,
                borderRadius: "50%",
              }}
            />
            <Typography variant="caption">Carbs {carbSplit}%</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                backgroundColor: NewColors.charts.blue,
                borderRadius: "50%",
              }}
            />
            <Typography variant="caption">Protein {proteinSplit}%</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                backgroundColor: NewColors.nutrition.fats,
                borderRadius: "50%",
              }}
            />
            <Typography variant="caption">Fat {fatSplit}%</Typography>
          </Box>
        </Box>
      </Box>
    );
  };
  function GetRoundedString(title, value) {
    if (isNaN(value + 1)) {
      return title + ": " + String(value);
    } else {
      return title + ": " + String(Math.round(value));
    }
  }
  function ChangeDifference({ title, value }) {
    if (title === "changedOn" || title === "updatedOn") {
      return null;
    }
    const finalValue = `${value}`;
    return (
      <Grid key={title} item sm={2} width={125}>
        {/* <Typography key={title} style={{ fontSize: 12 }}>
          {title}: {finalValue}
        </Typography> */}
        <Button
          key={title}
          style={{
            margin: 0,
            padding: 0,
            alignSelf: "left",
            justifyContent: "left",
            color: "black",
            fontSize: 12,
          }}
          onClick={() => {
            if (finalValue.length === 0) {
              setMacros({
                ...macros,
                title: "",
              });
              return;
            } else if (title === "name") {
              setMacros({
                ...macros,
                name: finalValue,
              });
            } else if (title === "carbs") {
              setMacros({
                ...macros,
                carbs: isNaN(finalValue) ? 0 : parseInt(finalValue, 10),
              });
            } else if (title === "proteins") {
              setMacros({
                ...macros,
                proteins: isNaN(finalValue) ? 0 : parseInt(finalValue, 10),
              });
            } else if (title === "fats") {
              setMacros({
                ...macros,
                fats: isNaN(finalValue) ? 0 : parseInt(finalValue, 10),
              });
            } else if (title === "water") {
              setMacros({
                ...macros,
                water: isNaN(finalValue) ? 0 : parseInt(finalValue, 10),
              });
            }
          }}
          onMouseOver={HighlightText}
          onMouseLeave={RemoveHighlight}
        >
          {GetRoundedString(title, finalValue)}
        </Button>
      </Grid>
    );
  }

  function HistoryChange({ change }) {
    const { differences } = change;
    return (
      <Grid container spacing={1}>
        <Grid item md={2}>
          <Button
            style={{
              color: "black",
              margin: 0,
              padding: 0,
              justifyContent: "left",
              textAlign: "left",
              fontSize: 12,
              fontWeight: "500",
              cursor: "default",
            }}
            disabled
          >
            {change.name}
          </Button>
        </Grid>

        {Object.keys(differences).map((key) => {
          return (
            <ChangeDifference key={key} title={key} value={differences[key]} />
          );
        })}
      </Grid>
    );
  }

  function HistoryItem({ history }) {
    if (!history || !history.changes || history.changes.length === 0) {
      return null;
    }
    return (
      <Box key={history.id} sx={{ mb: 1 }}>
        <Typography
          style={{
            color: NewColors.charts.blue,
            textAlign: "left",
            fontSize: 12,
            fontWeight: "bold",
          }}
        >
          {moment(history.changedOn.toDate()).format("LLL")}
        </Typography>
        <Box>
          {history.changes.map((change) => {
            return <HistoryChange key={change.id} change={change} />;
          })}
        </Box>
      </Box>
    );
  }

  function InteractiveHistory({ client, historyCollection, limit = 10 }) {
    const { history } = useHistory({
      uid: client.uid,
      historyCollection: historyCollection,
      limit: limit,
      name: nutritionGoal.name,
    });
    const revertString =
      "(Select a value to revert back to before that change)";

    return (
      <Box
        sx={{
          minWidth: 450,
          maxWidth: 450,
          maxHeight: 200,
          overflowY: "scroll",
        }}
      >
        <Typography sx={{ fs: 14, fontWeight: "bold" }}>
          Recent History
        </Typography>
        <Typography
          sx={{
            fontSize: 13,
            fs: 14,
            fontStyle: "italic",
            mb: 1,
          }}
        >
          {revertString}
        </Typography>
        <Box>
          {history?.map((h) => {
            return <HistoryItem key={h.id} history={h} />;
          })}
        </Box>
      </Box>
    );
  }

  return (
    <Dialog
      maxWidth="lg"
      PaperProps={{
        sx: {
          minHeight: 800,
          minWidth: 800,
        },
      }}
      open={openDialog}
      onClose={() => {
        if (onDialogClosed) {
          onDialogClosed(null);
        }
      }}
    >
      <DialogTitle component="div">
        <Typography
          variant="h5"
          component="h2"
          sx={{ fontWeight: "bold", color: NewColors.charts.blue }}
        >
          Select Macros
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Quick Setup" />
            <Tab label="Calculator" />
            <Tab label="Manual" />
            {isEdit && <Tab label="History" />}
          </Tabs>
        </Box>

        {/* Goal Name Field - Always visible */}
        <FormField
          label="Goal Name"
          required
          fullWidth
          sx={{ mb: 0 }}
          error={!!validationErrors.name}
          helperText={validationErrors.name}
        >
          <TextField
            value={macros.name}
            fullWidth
            variant="outlined"
            placeholder="Enter a name for this nutrition goal"
            error={!!validationErrors.name}
            onChange={(event) => {
              setMacros({
                ...macros,
                name: event.target.value,
              });
              // Clear name validation error when user starts typing
              if (validationErrors.name) {
                setValidationErrors((prev) => ({ ...prev, name: undefined }));
              }
            }}
          />
        </FormField>

        {/* Validation Alert */}
        {validationErrors.macros && (
          <Box sx={{ mb: 1 }}>
            <Card
              sx={{
                backgroundColor: NewColors.cardBackground,
                border: `1px solid ${NewColors.brand.primary}`,
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "bold", color: NewColors.brand.primary, mb: 1 }}
                >
                  ⚠️ Validation Required
                </Typography>
                <Typography variant="body2" sx={{ color: NewColors.text.primary }}>
                  {validationErrors.macros}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Tab Content */}
        <TabPanel value={activeTab} index={0}>
          <Box>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: "bold" }}>
              Choose a Preset Macro Ratio
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Or use the Calculator tab for intelligent recommendations based on
              your client&rsquo;s body composition and goals.
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {MACRO_PRESETS.map((preset) => (
                <Grid item xs={12} sm={6} md={3} key={preset.name}>
                  <PresetCard
                    preset={preset}
                    isSelected={selectedPreset === preset.name}
                    onClick={() => handlePresetSelect(preset)}
                  />
                </Grid>
              ))}
            </Grid>
            {selectedPreset && (
              <Card sx={{ backgroundColor: NewColors.cardBackground, p: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: "bold", mb: 1 }}>
                  Selected: {selectedPreset}
                </Typography>
                <MacroDistributionPreview />
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleCalculateMacros}
                    disabled={macroSplitTotal !== 100}
                    sx={{ mr: 2 }}
                  >
                    Calculate Grams
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    Click &ldquo;Calculate Grams&rdquo; to generate macro
                    amounts based on your client&rsquo;s stats
                  </Typography>
                </Box>
              </Card>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Box sx={{ p: 0 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
              Macro Calculator
            </Typography>
            <Grid container spacing={1.5}>
              {/* Left Column: Settings & Stats */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  {/* Method Settings Card */}
                  <Card sx={{ p: 2, boxShadow: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        mb: 1.5,
                        color: NewColors.charts.blue,
                      }}
                    >
                      📊 Calculation Method
                    </Typography>
                    <RadioGroup
                      value={method}
                      onChange={handleMethodChange}
                      sx={{ mb: 1 }}
                    >
                      <FormControlLabel
                        value="body-fat"
                        control={<Radio size="small" />}
                        label={
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              Body Fat % (More Accurate)
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Uses lean body mass calculation
                            </Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="age-weight"
                        control={<Radio size="small" />}
                        label={
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              Age/Weight/Height
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Standard BMR formula
                            </Typography>
                          </Box>
                        }
                      />
                    </RadioGroup>

                    <TextField
                      label="Body Fat %"
                      value={fatPercentage}
                      type="number"
                      size="small"
                      fullWidth
                      inputProps={{ min: 5, max: 50 }}
                      onChange={handleFatPercentageChange}
                      helperText="Typical ranges: Athletes 6-13%, Fitness 14-17%, Average 18-25%"
                      sx={{ mt: 1 }}
                    />
                  </Card>

                  {/* Weight Goal Card */}
                  <Card sx={{ p: 2, boxShadow: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        mb: 1.5,
                        color: NewColors.nutrition.fats,
                      }}
                    >
                      🎯 Weight Goal
                    </Typography>
                    <Select
                      value={weightGoal}
                      onChange={handleWeightGoalChange}
                      size="small"
                      fullWidth
                    >
                      <MenuItem value={WeightGoal.lose}>
                        🔥 {WeightGoalLabel.lose}
                      </MenuItem>
                      <MenuItem value={WeightGoal.lose_light}>
                        ⬇️ {WeightGoalLabel.lose_light}
                      </MenuItem>
                      <MenuItem value={WeightGoal.maintain}>
                        ⚖️ {WeightGoalLabel.maintain}
                      </MenuItem>
                      <MenuItem value={WeightGoal.gain_light}>
                        ⬆️ {WeightGoalLabel.gain_light}
                      </MenuItem>
                      <MenuItem value={WeightGoal.gain}>
                        💪 {WeightGoalLabel.gain}
                      </MenuItem>
                    </Select>
                  </Card>

                  {/* Client Information Card */}
                  <Card sx={{ p: 2, boxShadow: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, mb: 1.5, color: NewColors.text.primary }}
                    >
                      👤 Client Information
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            textAlign: "center",
                            p: 1,
                            backgroundColor: NewColors.cardBackground,
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Activity
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              fontSize: "0.8rem",
                              display: "block",
                              minHeight: "1.2em",
                            }}
                          >
                            {client.statistics?.activityLevel
                              ? ActivityLevelLabel[
                                  client.statistics.activityLevel
                                ]
                              : ActivityLevelLabel[ActivityLevel.sedentary]}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            textAlign: "center",
                            p: 1,
                            backgroundColor: NewColors.cardBackground,
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Gender
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {calculatedValues.sex}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box
                          sx={{
                            textAlign: "center",
                            p: 1,
                            backgroundColor: NewColors.cardBackground,
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Age
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              color:
                                calculatedValues.age === 0
                                  ? "error.main"
                                  : "text.primary",
                            }}
                          >
                            {calculatedValues.age}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box
                          sx={{
                            textAlign: "center",
                            p: 1,
                            backgroundColor: NewColors.cardBackground,
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Weight
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              color:
                                calculatedValues.weightKg === 0
                                  ? "error.main"
                                  : "text.primary",
                            }}
                          >
                            {calculatedValues.weightKg} kg
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box
                          sx={{
                            textAlign: "center",
                            p: 1,
                            backgroundColor: NewColors.cardBackground,
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Height
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              color:
                                calculatedValues.height === 0
                                  ? "error.main"
                                  : "text.primary",
                            }}
                          >
                            {calculatedValues.height.toFixed(0)} cm
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Card>
                </Box>
              </Grid>

              {/* Right Column: Results & Macro Controls */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  {/* Calculation Results Card */}
                  <Card
                    sx={{
                      p: 2,
                      boxShadow: 2,
                      border: `2px solid ${NewColors.charts.blue}`,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, mb: 2, color: NewColors.charts.blue }}
                    >
                      ⚡ Calorie Calculation Flow
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ flex: 1, textAlign: "center" }}>
                        <Box
                          sx={{
                            p: 1.5,
                            backgroundColor: NewColors.charts.blue,
                            color: "white",
                            borderRadius: 2,
                            boxShadow: 1,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ display: "block", opacity: 0.9 }}
                          >
                            BMR
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                            {method === "body-fat"
                              ? calculatedValues.bmr2.toFixed(0)
                              : calculatedValues.bmr1.toFixed(0)}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="h6" sx={{ color: NewColors.text.secondary }}>
                        →
                      </Typography>
                      <Box sx={{ flex: 1, textAlign: "center" }}>
                        <Box
                          sx={{
                            p: 1.5,
                            backgroundColor: NewColors.nutrition.carbs,
                            color: "white",
                            borderRadius: 2,
                            boxShadow: 1,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ display: "block", opacity: 0.9 }}
                          >
                            Adjusted
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                            {method === "body-fat"
                              ? calculatedValues.adjustedbmr2.toFixed(0)
                              : calculatedValues.adjustedbmr1.toFixed(0)}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="h6" sx={{ color: NewColors.text.secondary }}>
                        →
                      </Typography>
                      <Box sx={{ flex: 1, textAlign: "center" }}>
                        <Box
                          sx={{
                            p: 1.5,
                            backgroundColor: NewColors.nutrition.fats,
                            color: "white",
                            borderRadius: 2,
                            boxShadow: 1,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ display: "block", opacity: 0.9 }}
                          >
                            Goal
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                            {method === "body-fat"
                              ? calculatedValues.bmr2WeightFactor.toFixed(0)
                              : calculatedValues.bmr1WeightFactor.toFixed(0)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Card>

                  {/* Macro Split Card */}
                  <Card sx={{ p: 2, boxShadow: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: NewColors.nutrition.carbs }}
                      >
                        🍎 Macro Split Percentages
                      </Typography>
                      {!selectedPreset && (
                        <Chip
                          size="small"
                          label="Auto-adjusted"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </Box>

                    <Grid container spacing={1} sx={{ mb: 1.5 }}>
                      <Grid item xs={4}>
                        <TextField
                          label="Carbs %"
                          value={carbSplit}
                          type="number"
                          size="small"
                          fullWidth
                          onChange={(event) =>
                            setCarbSplit(parseInt(event.target.value) || 0)
                          }
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          label="Protein %"
                          value={proteinSplit}
                          type="number"
                          size="small"
                          fullWidth
                          onChange={(event) =>
                            setProteinSplit(parseInt(event.target.value) || 0)
                          }
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          label="Fat %"
                          value={fatSplit}
                          type="number"
                          size="small"
                          fullWidth
                          onChange={(event) =>
                            setFatSplit(parseInt(event.target.value) || 0)
                          }
                        />
                      </Grid>
                    </Grid>

                    {/* Total & Validation */}
                    <Box sx={{ textAlign: "center", mb: 1.5 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: "bold",
                          color:
                            macroSplitTotal !== 100
                              ? "error.main"
                              : "success.main",
                        }}
                      >
                        Total: {macroSplitTotal}%{" "}
                        {macroSplitTotal !== 100 && "(Must equal 100%)"}
                      </Typography>
                    </Box>

                    {/* Macro Distribution Preview */}
                    <MacroDistributionPreview />
                  </Card>

                  {/* Smart Recommendations Card */}
                  {!selectedPreset && (
                    <Card
                      sx={{
                        p: 2,
                        boxShadow: 1,
                        backgroundColor: NewColors.cardBackground,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: NewColors.charts.blue,
                          mb: 1,
                        }}
                      >
                        💡 Smart Recommendations
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: NewColors.text.primary, fontSize: "0.85rem" }}
                      >
                        {method === "body-fat" &&
                          parseFloat(fatPercentage) < 15 &&
                          "Higher carbs recommended for lean individuals to support performance."}
                        {method === "body-fat" &&
                          parseFloat(fatPercentage) > 25 &&
                          "Lower carbs, higher protein recommended for effective fat loss."}
                        {weightGoal === WeightGoal.lose &&
                          "High protein, low carb approach for aggressive fat loss while preserving muscle."}
                        {weightGoal === WeightGoal.lose_light &&
                          "Moderate protein increase supports sustainable fat loss."}
                        {weightGoal === WeightGoal.gain &&
                          "Higher carbs provide energy needed for muscle building workouts."}
                        {weightGoal === WeightGoal.gain_light &&
                          "Balanced approach promotes lean muscle gain without excess fat."}
                        {weightGoal === WeightGoal.maintain &&
                          "Balanced ratios support weight maintenance and general health."}
                      </Typography>
                    </Card>
                  )}

                  {/* Action Buttons */}
                  <Box
                    sx={{ display: "flex", gap: 1, justifyContent: "center" }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setSelectedPreset(null);
                        const intelligentSplit = getIntelligentMacroSplit();
                        setCarbSplit(intelligentSplit.carbs);
                        setProteinSplit(intelligentSplit.protein);
                        setFatSplit(intelligentSplit.fat);
                      }}
                    >
                      Reset
                    </Button>
                    <Button
                      disabled={macroSplitTotal !== 100}
                      variant="contained"
                      onClick={handleCalculateMacros}
                      sx={{ backgroundColor: NewColors.nutrition.fats }}
                    >
                      Calculate Grams
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                Manual Macro Entry
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormField
                    label="Carbs (g)"
                    error={!!validationErrors.carbs}
                    helperText={validationErrors.carbs}
                    sx={{ mb: 2 }}
                  >
                    <TextField
                      type="number"
                      value={macros.carbs ? macros.carbs.toFixed(0) : ""}
                      fullWidth
                      variant="outlined"
                      error={!!validationErrors.carbs}
                      inputProps={{ min: 0 }}
                      onChange={(event) => {
                        setMacros({
                          ...macros,
                          carbs:
                            event.target.value === ""
                              ? 0
                              : parseInt(event.target.value, 10) || 0,
                        });
                        // Clear validation errors when user enters values
                        if (validationErrors.carbs || validationErrors.macros) {
                          setValidationErrors((prev) => ({
                            ...prev,
                            carbs: undefined,
                            macros: undefined,
                          }));
                        }
                      }}
                    />
                  </FormField>
                </Grid>
                <Grid item xs={6}>
                  <FormField
                    label="Proteins (g)"
                    error={!!validationErrors.proteins}
                    helperText={validationErrors.proteins}
                    sx={{ mb: 2 }}
                  >
                    <TextField
                      type="number"
                      value={macros.proteins ? macros.proteins.toFixed(0) : ""}
                      fullWidth
                      variant="outlined"
                      error={!!validationErrors.proteins}
                      inputProps={{ min: 0 }}
                      onChange={(event) => {
                        setMacros({
                          ...macros,
                          proteins:
                            event.target.value === ""
                              ? 0
                              : parseInt(event.target.value, 10) || 0,
                        });
                        // Clear validation errors when user enters values
                        if (
                          validationErrors.proteins ||
                          validationErrors.macros
                        ) {
                          setValidationErrors((prev) => ({
                            ...prev,
                            proteins: undefined,
                            macros: undefined,
                          }));
                        }
                      }}
                    />
                  </FormField>
                </Grid>
                <Grid item xs={6}>
                  <FormField
                    label="Fats (g)"
                    error={!!validationErrors.fats}
                    helperText={validationErrors.fats}
                    sx={{ mb: 2 }}
                  >
                    <TextField
                      type="number"
                      value={macros.fats ? macros.fats.toFixed(0) : ""}
                      fullWidth
                      variant="outlined"
                      error={!!validationErrors.fats}
                      inputProps={{ min: 0 }}
                      onChange={(event) => {
                        setMacros({
                          ...macros,
                          fats:
                            event.target.value === ""
                              ? 0
                              : parseInt(event.target.value, 10) || 0,
                        });
                        // Clear validation errors when user enters values
                        if (validationErrors.fats || validationErrors.macros) {
                          setValidationErrors((prev) => ({
                            ...prev,
                            fats: undefined,
                            macros: undefined,
                          }));
                        }
                      }}
                    />
                  </FormField>
                </Grid>
                <Grid item xs={6}>
                  <FormField
                    label={isImperial ? "Water (oz)" : "Water (mL)"}
                    error={!!validationErrors.water}
                    helperText={
                      validationErrors.water ||
                      (isImperial ? "Rec: 68–135 oz/day" : "Rec: 2000-4000 mL/day")
                    }
                    sx={{ mb: 2 }}
                  >
                    <TextField
                      type="number"
                      value={macros.water || ""}
                      fullWidth
                      variant="outlined"
                      error={!!validationErrors.water}
                      inputProps={{
                        min: 0,
                        max: isImperial ? 338 : 10000,
                        step: isImperial ? 0.1 : 1,
                      }}
                      onChange={(event) => {
                        const raw = event.target.value;
                        const parsed =
                          raw === ""
                            ? 0
                            : isImperial
                              ? parseFloat(raw) || 0
                              : parseInt(raw, 10) || 0;
                        setMacros({
                          ...macros,
                          water: parsed,
                        });
                        // Clear validation error when user corrects value
                        if (validationErrors.water) {
                          setValidationErrors((prev) => ({
                            ...prev,
                            water: undefined,
                          }));
                        }
                      }}
                    />
                  </FormField>
                </Grid>
                <Grid item xs={6}>
                  <FormField
                    label="Fiber (g)"
                    error={!!validationErrors.fiber}
                    helperText={
                      validationErrors.fiber || "Optional daily fiber target"
                    }
                    sx={{ mb: 2 }}
                  >
                    <TextField
                      type="number"
                      value={macros.fiber != null && macros.fiber !== "" ? macros.fiber : ""}
                      fullWidth
                      variant="outlined"
                      error={!!validationErrors.fiber}
                      inputProps={{ min: 0, max: 200, step: 0.1 }}
                      onChange={(event) => {
                        const raw = event.target.value;
                        setMacros({
                          ...macros,
                          fiber:
                            raw === ""
                              ? 0
                              : parseFloat(raw) || 0,
                        });
                        if (validationErrors.fiber) {
                          setValidationErrors((prev) => ({
                            ...prev,
                            fiber: undefined,
                          }));
                        }
                      }}
                    />
                  </FormField>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  p: 3,
                  backgroundColor: NewColors.cardBackground,
                  height: "fit-content",
                }}
              >
                <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                  Nutrition Summary
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography>Total Calories:</Typography>
                  <Typography
                    sx={{ fontWeight: "bold", color: NewColors.charts.blue }}
                  >
                    {totalkCal.toFixed(0)} kcal
                  </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography>Carbohydrates:</Typography>
                  <Typography>
                    {macros.carbs || 0}g ({((macros.carbs || 0) * 4).toFixed(0)}{" "}
                    kcal)
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography>Proteins:</Typography>
                  <Typography>
                    {macros.proteins || 0}g (
                    {((macros.proteins || 0) * 4).toFixed(0)} kcal)
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography>Fats:</Typography>
                  <Typography>
                    {macros.fats || 0}g ({((macros.fats || 0) * 9).toFixed(0)}{" "}
                    kcal)
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography>Water:</Typography>
                  <Typography>
                    {macros.water || 0} {isImperial ? "oz" : "mL"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography>Fiber:</Typography>
                  <Typography>
                    {macros.fiber ? `${macros.fiber} g` : "—"}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {isEdit && (
          <TabPanel value={activeTab} index={3}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
              Goal History
            </Typography>
            <InteractiveHistory
              client={client}
              historyCollection={"nutritionGoalHistory"}
            />
          </TabPanel>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={() => {
            setOpenDialog(false);
            if (onDialogClosed) {
              onDialogClosed(null);
            }
          }}
          color="primary"
        >
          Cancel
        </Button>
        <StandardButton
          variant="primary"
          onClick={() => {
            if (validateForm()) {
              setOpenDialog(false);
              if (onDialogClosed) {
                onDialogClosed({
                  id: macros.id,
                  name: macros.name,
                  proteins: macros.proteins,
                  carbs: macros.carbs,
                  fats: macros.fats,
                  water: isImperial
                    ? Math.round((macros.water || 0) * 29.5735)
                    : macros.water,
                  fiber: macros.fiber || 0,
                  active: macros.active ? macros.active : false,
                });
              }
            }
            // Validation errors are already set by validateForm(), no need for additional error handling
          }}
          disabled={
            Object.keys(validationErrors).length > 0 && !macros.name?.trim()
          }
        >
          Save
        </StandardButton>
      </DialogActions>
    </Dialog>
  );
};

export default MacroSelectSimpleDialog;
